import React, { useState, useEffect } from 'react';
import './PlanConfig.css';
import Navbar from '../../components/Navbar/Navbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { getPlanSelection, clearPlanSelection, setCookie } from '../../utils/cookies';
import usePlanExpiredPopupStore from '../../store/Useplanexpiredpopupstore';
import { getPlanConfig, getCurrentPlan, createRazorpayOrder, verifyRazorpayPayment } from '../../services/subscriptionApis';
import { SuccessAlert } from '../../components/common/ErrorAlert/SuccessAlert';


function fmt(n) {
    return '₹' + Math.round(n).toLocaleString('en-IN');
}

const ArrowRight = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="sub-checkout-proceed-btn-icon">
        <path d="M3.33337 8H12.6667" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 3.33301L12.6667 7.99967L8 12.6663" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export default function PlanCustomize() {
    const navigate = useNavigate();
    const location = useLocation();

    // ── State ──────────────────────────────────────────────────────────────
    const [billing, setBilling] = useState('yearly');
    const [qty, setQty] = useState({ users: 0, maps: 0, ai: 0 });

    const [planDetails, setPlanDetails] = useState(null);      // from /pricing/configure/:id
    const [currentPlan, setCurrentPlan] = useState(null);      // from /subscriptions/current-plan/
    const [addonPrices, setAddonPrices] = useState(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const stateData = location.state;
    const { planId: cookiePlanId, billingCycle: cookieCycle } = getPlanSelection();
    const planId = stateData?.planId || cookiePlanId;

    // ── Load Razorpay SDK ──────────────────────────────────────────────────
    useEffect(() => {
        if (document.getElementById('razorpay-sdk')) return;
        const script = document.createElement('script');
        script.id = 'razorpay-sdk';
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
    }, []);

    // ── Fetch both APIs on mount ───────────────────────────────────────────
    useEffect(() => {
        if (!planId) { navigate('/home'); return; }

        const cookieBilling = stateData?.billingCycle || cookieCycle;
        const cycle = (cookieBilling === 'monthly' || cookieBilling === 'yearly')
            ? cookieBilling : 'yearly';
        setBilling(cycle);

        const isRenewal = stateData?.is_renewal ?? false;

        const fetchAll = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};

                const [plan, cpData] = await Promise.all([
                    getPlanConfig(planId),
                    getCurrentPlan().then(r => r?.data || null),
                ]);

                // const plan = planRes.data;
                if (plan.is_freeplan) { navigate('/home'); return; }
                setPlanDetails(plan);

                setAddonPrices({
                    yearly: {
                        users: parseFloat(plan.addon_prices.yearly.users),
                        maps:  parseFloat(plan.addon_prices.yearly.maps),
                        ai:    parseFloat(plan.addon_prices.yearly.ai),
                    },
                    monthly: {
                        users: parseFloat(plan.addon_prices.monthly.users),
                        maps:  parseFloat(plan.addon_prices.monthly.maps),
                        ai:    parseFloat(plan.addon_prices.monthly.ai),
                    },
                });

                // const cpData = currentPlanRes.data?.data || null;
                setCurrentPlan(cpData);

                // Billing cycle from current plan, not cookies
                const cycle = cpData?.billing_cycle === 'monthly' ? 'monthly' : 'yearly';
                setBilling(cycle);

                // Steppers always start at 0 — this is "add more" page
                setQty({ users: 0, maps: 0, ai: 0 });

            } catch (err) {
                console.error('Failed to fetch plan data:', err);
                setError('Failed to load plan. Please refresh and try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAll();
    }, [planId]);

    
    // ── Derived values ─────────────────────────────────────────────────────
    const p         = addonPrices?.[billing] ?? { users: 0, maps: 0, ai: 0 };
    const planName  = planDetails?.display_name || '—';
    const planLabel = billing === 'yearly' ? 'Annual Billing' : 'Monthly Billing';

    // Base price is 0 — user already paid, this page is for adding more
    const basePrice = 0;

    // Current limits from API (the baseline they already have)
    const currentUserLimit = currentPlan?.user_limit       ?? 0;
    const currentMapsLimit = currentPlan?.maps_calls_limit ?? 0;
    const currentAILimit   = currentPlan?.ai_credits_limit ?? 0;

    // Live totals = current limits + what they're adding now
    const totalUsers = currentUserLimit + qty.users;
    const totalMaps  = currentMapsLimit + qty.maps * 1000;
    const totalAI    = currentAILimit   + qty.ai   * 1000;

    const parseStatus = (statusStr) => {
        if (!statusStr) return { used: 0, total: 0 };
        const [u, t] = statusStr.split('/').map(Number);
        return { used: u || 0, total: t || 0 };
    };

    // Usage from API — stays fixed, only the total changes
    const userStatus = parseStatus(currentPlan?.user_limit_status);
    const mapsStatus = { used: currentPlan?.maps_calls_used ?? 0 };
    const aiStatus   = { used: currentPlan?.ai_credits_used  ?? 0 };

    // Only addons accumulate — base is 0
    const userCost = qty.users * p.users;
    const mapsCost = qty.maps  * p.maps;
    const aiCost   = qty.ai    * p.ai;
    const total    = userCost + mapsCost + aiCost;   // ← no basePrice here


    const getQuotaState = (used, total) =>
        (used === 0 || used >= total) ? 'danger' : 'safe';

    const quotaStats = [
        { label: 'Users',      used: userStatus.used, total: totalUsers },
        { label: 'Map Calls', used: mapsStatus.used,  total: totalMaps  },
        { label: 'AI Credits', used: aiStatus.used,    total: totalAI    },
    ];

    // ── Handlers ──────────────────────────────────────────────────────────
    const change = (key, delta) =>
        setQty(prev => ({ ...prev, [key]: Math.max(0, prev[key] + delta) }));

    const handleBillingChange = (cycle) => {
        setBilling(cycle);
        setCookie('selected_billing_cycle', cycle, 7);
    };

    // ── Razorpay ───────────────────────────────────────────────────────────
    const openRazorpay = (orderData, token) => {
        if (!window.Razorpay) {
            setError('Razorpay SDK not loaded. Please refresh the page.');
            setIsSubmitting(false);
            return;
        }

        const options = {
            key: orderData.key,
            amount: orderData.amount,
            currency: orderData.currency || 'INR',
            name: 'SalesRoute',
            description: `${orderData.plan_name || planName} – ${orderData.billing_label || planLabel}`,
            order_id: orderData.razorpay_order_id,

            handler: async (response) => {
                try {
                    await verifyRazorpayPayment({
                        ...response,
                        subscription_id: orderData.subscription_id,
                    });
                    clearPlanSelection();
                    usePlanExpiredPopupStore.getState().clearPlanExpiredPopup();
                    navigate('/home');
                    SuccessAlert("Plan customized successfully")
                } catch (err) {
                    setError(err.response?.data?.message || 'Payment verification failed. Please contact support.');
                } finally {
                    setIsSubmitting(false);
                }
            },

            modal: {
                ondismiss: () => {
                    setIsSubmitting(false);
                    setError('Payment was cancelled. You can try again.');
                },
            },

            prefill: { email: localStorage.getItem('userEmail') || '' },
            theme: { color: '#0080D7' },
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (response) => {
            setError(response.error?.description || 'Payment failed. Please try again.');
            setIsSubmitting(false);
        });
        rzp.open();
    };

    // ── Continue to payment ────────────────────────────────────────────────
    const handleContinue = async () => {
        setError('');
        setIsSubmitting(true);

        const token = localStorage.getItem('accessToken');
        if (!token) { setError('Session expired. Please login again.'); setIsSubmitting(false); return; }

        // Guard: nothing added, nothing to pay
        if (qty.users === 0 && qty.maps === 0 && qty.ai === 0) {
            setError('Please add at least one add-on to continue.');
            setIsSubmitting(false);
            return;
        }

        const payload = {
            plan_id:          currentPlan?.plan_id,
            billing_cycle:    billing,
            extra_users:      qty.users,
            extra_maps:       qty.maps,
            extra_ai:         qty.ai,
            currency:         'INR',
            activate_after:   '',
            is_upgrade:       false,
            is_addon:         true,
            old_sub_id:       '',
            proration_credit: 0,
        };

        try {
            const orderData = await createRazorpayOrder(payload);
            if (orderData.razorpay_order_id) { openRazorpay(orderData, token); return; }
            if (orderData.payment_url) { clearPlanSelection(); window.location.href = orderData.payment_url; return; }
            navigate('/payment', { state: { orderData, config: payload } });
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to create order. Please try again.');
            setIsSubmitting(false);
        }
    };

    // ── Loading ────────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="app">
                <Navbar />
                <div className="checkout-stepper-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <p style={{ color: '#475569', fontFamily: 'Poppins, sans-serif' }}>Loading plan details…</p>
                </div>
            </div>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="app">
            <Navbar />

            <div className="checkout-stepper-wrapper">

                {/* ── Stepper ── */}
                <div className="checkout-stepper">
                    <div className="checkout-step done">
                        <div className="checkout-step-bubble active">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13.3333 4L5.99999 11.3333L2.66666 8" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        Plan
                    </div>
                    <div className="checkout-step-connector active" />
                    <div className="checkout-step active">
                        <div className="checkout-step-bubble active">2</div>
                        Configure
                    </div>
                    <div className="checkout-step-connector" />
                    <div className="checkout-step">
                        <div className="checkout-step-bubble">3</div>
                        Payment
                    </div>
                </div>

                <div className="sub-configure-container">

                    {/* ── Header ── */}
                    <div className="sub-configure-header">
                        <div className="sub-configure-header-left">
                            <h1>Configure Your Plan</h1>
                            <p className="sub-configure-subtitle">Customize your setup based on your business needs</p>
                        </div>

                        <div className="billing-cycle-toggle show-only">
                            <div className="billing-cycle-option-wrap">
                                <input
                                    type="radio" name="billing" id="billing-yearly"
                                    checked={billing === 'yearly'}
                                    onChange={() => handleBillingChange('yearly')}
                                />
                                <label htmlFor="billing-yearly" className="billing-cycle-option-label">
                                    Yearly <span className="billing-cycle-savings-badge">(Save 30%)</span>
                                </label>
                            </div>
                            <div className="billing-cycle-option-wrap">
                                <input
                                    type="radio" name="billing" id="billing-monthly"
                                    checked={billing === 'monthly'}
                                    onChange={() => handleBillingChange('monthly')}
                                />
                                <label htmlFor="billing-monthly" className="billing-cycle-option-label">
                                    Monthly
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="sub-configure-body">

                        {/* ── Left Column ── */}
                        <div className="sub-configure-left-col">

                            {/* Selected Plan Card */}
                            <div className="selected-plan-card top">
                                {/* <div className="selected-plan-card-header">
                                    <div>
                                        <div className="selected-plan-label">Selected Plan</div>
                                        <div className="selected-plan-details">
                                            <span className="selected-plan-name">{planName}</span>
                                            <span className="selected-plan-price">{fmt(currentPlan?.amount_paid)}</span>
                                            <span className="selected-plan-label">
                                                /{billing === 'yearly' ? 'year' : 'month'}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        className="cost-change-plan-btn"
                                        onClick={() => navigate('/plans')}
                                    >
                                        Change plan
                                    </button>
                                </div> */}

                                <div className="selected-plan-card-header">
                                    <div>
                                        <div className="selected-plan-label">Selected Plan</div>
                                        <div className="selected-plan-name">{planName}</div>
                                    </div>
                                    <div className="selected-plan-pricing">
                                        <div className="selected-plan-price-amount">{fmt(currentPlan?.amount_paid)}</div>
                                        <div className="selected-plan-billing-period">{planLabel}</div>
                                    </div>
                                </div>

                                <div className="selected-plan-inclusions">
                                    <span className="selected-plan-inclusions-label">Plan Includes:</span>
                                    <div className="plan-quota-stats-row">
                                        {quotaStats.map(({ label, used, total: statTotal }) => {
                                            const state = getQuotaState(used, statTotal);
                                            return (
                                                <div key={label} className="plan-quota-stat-box">
                                                    <div className="plan-quota-stat-value">
                                                        <span className={`quota-used ${state}`}>
                                                            {used.toLocaleString('en-IN')}
                                                        </span>
                                                        <span className={`quota-slash ${state}`}>/</span>
                                                        <span className="quota-total">
                                                            {statTotal.toLocaleString('en-IN')}
                                                        </span>
                                                    </div>
                                                    <div className="plan-quota-stat-label">{label}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Add-ons Card */}
                            <div className="plan-addons-card">
                                <div className="plan-addons-list">

                                    {/* Users */}
                                    <div className="plan-addon-row">
                                        <div className="plan-addon-row-inner">
                                            <div className="plan-addon-info">
                                                <div className="plan-addon-title">Users</div>
                                                <div className="plan-addon-description">Add team members to manage sales and operations</div>
                                            </div>
                                            <div className="addon-qty-stepper">
                                                <button className="addon-qty-stepper-btn" onClick={() => change('users', -1)}>−</button>
                                                <div className="addon-qty-value">{qty.users}</div>
                                                <button className="addon-qty-stepper-btn" onClick={() => change('users', 1)}>+</button>
                                            </div>
                                            <div className="plan-addon-unit-price">
                                                {p.users > 0 ? `₹${p.users} / user / month` : 'Included'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Maps */}
                                    <div className="plan-addon-row">
                                        <div className="plan-addon-row-inner">
                                            <div className="plan-addon-info">
                                                <div className="plan-addon-title">
                                                    Maps Usage <span className="plan-addon-title-note">(in 1000 API calls)</span>
                                                </div>
                                                <div className="plan-addon-description">Used for tracking and route optimization</div>
                                            </div>
                                            <div className="addon-qty-stepper">
                                                <button className="addon-qty-stepper-btn" onClick={() => change('maps', -1)}>−</button>
                                                <div className="addon-qty-value">{qty.maps}</div>
                                                <button className="addon-qty-stepper-btn" onClick={() => change('maps', 1)}>+</button>
                                            </div>
                                            <div className="plan-addon-unit-price">
                                                {p.maps > 0 ? `₹${p.maps} per 1000 calls` : 'Included'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* AI Credits */}
                                    <div className="plan-addon-row">
                                        <div className="plan-addon-row-inner">
                                            <div className="plan-addon-info">
                                                <div className="plan-addon-title">AI Credits</div>
                                                <div className="plan-addon-description">Used for AI summaries, suggestions, and automation</div>
                                            </div>
                                            <div className="addon-qty-stepper">
                                                <button className="addon-qty-stepper-btn" onClick={() => change('ai', -1)}>−</button>
                                                <div className="addon-qty-value">{qty.ai}</div>
                                                <button className="addon-qty-stepper-btn" onClick={() => change('ai', 1)}>+</button>
                                            </div>
                                            <div className="plan-addon-unit-price">
                                                {p.ai > 0 ? `₹${p.ai} per 1000 credits` : 'Included'}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* ── Right Column — Summary ── */}
                        <div className="order-summary-wrapper">
                            <div className="order-summary-card">
                                <div className="order-summary-card-frame">
                                    <div className="order-summary-heading">Summary</div>

                                    <div className="order-summary-line-items">
                                        <div className="order-summary-line-group">
                                            {/* No base plan line — they already paid. Only show addons. */}
                                            <div className="order-summary-line-item">
                                                <span className="order-summary-item-label">{planName}</span>
                                                <span className="order-summary-item-amount">{fmt(basePrice)}</span>
                                            </div>
                                            <div className="order-summary-line-item">
                                                <span className="order-summary-item-label">Users add-on</span>
                                                <span className="order-summary-item-amount">{fmt(userCost)}</span>
                                            </div>
                                            <div className="order-summary-line-item">
                                                <span className="order-summary-item-label">Maps add-on</span>
                                                <span className="order-summary-item-amount">{fmt(mapsCost)}</span>
                                            </div>
                                            <div className="order-summary-line-item">
                                                <span className="order-summary-item-label">AI Credits add-on</span>
                                                <span className="order-summary-item-amount">{fmt(aiCost)}</span>
                                            </div>
                                        </div>


                                        {/* Total */}
                                        <div className="order-summary-total-row">
                                            <span className="order-summary-total-label">Total Amount</span>
                                            <span className="order-summary-total-amount">{fmt(total)}</span>
                                        </div>
                                    </div>

                                    {/* Savings banner */}
                                    {billing === 'yearly' ?
                                    <div className="order-summary-coupon-banner">
                                        <span>
                                            Congrats! You've saved 30%
                                        </span>
                                    </div>
                                    : null}

                                    {error && (
                                        <p className="plan-config-error-message">
                                            {error}
                                        </p>
                                    )}

                                    <button
                                        className="sub-checkout-proceed-btn"
                                        onClick={handleContinue}
                                        disabled={isSubmitting}
                                        style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                                    >
                                        {isSubmitting ? 'Processing…' : 'Continue to Payment'}
                                        {!isSubmitting && <ArrowRight />}
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}